import styled, { css } from "styled-components";
import type { GridStyledProps } from "../types";

interface GridColorProps {
  $bgColor: string;
}

export const GridColorStyled = styled.div<GridColorProps>(({ $bgColor }) => ({
  background: $bgColor || `var(--theme-color-3)`,
}));

export const WrapperStyled = styled.div<{ $align: string; $type: string }>`
  > svg {
    pointer-events: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    opacity: 0;
    z-index: -10000;
  }
  .wrapper {
    position: relative;
    display: flex;
    justify-content: ${({ $align }) => $align};
    align-items: center;
    width: 100%;
  }
  ${({ $type }) =>
    $type !== "grid-6" && $type !== "grid-7" && $type !== "grid-5"
      ? css`
          .resize-controls {
            .counter {
              opacity: 1;
              visibility: visible;
              bottom: 0;
              transform: translate(-50%, 50%);
            }
            .height {
              display: none;
            }
          }
        `
      : ""}
`;
export const GridStyled = styled.div<GridStyledProps>`
  --gap: ${({ $gap }) => Number($gap) + "px"};

  width: var(--entity-size);
  /* padding: var(--gap); */
  .grid-inner {
    overflow: hidden;
  }
  .grid-container {
    display: flex;
    overflow: hidden;
    flex-wrap: wrap;
    justify-content: center;
    width: auto;
    margin: calc(var(--gap) / -2);
  }
  .item {
    position: relative;
    overflow: hidden;
    border-radius: ${({ $radius }) => $radius};
    ${({ $setsPerLine }) => `
      flex: 0 0 calc(100% / ${$setsPerLine} - var(--gap));
      padding-top: calc(100% / ${$setsPerLine} - var(--gap));
    `}
    margin: calc(var(--gap) / 2);
    ${({ $radius }) =>
      $radius === "32px" &&
      css`
        #grid-variables-label {
          right: 32px;
        }
      `}
    > * {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &.grid-2 {
    .item {
      ${({ $setsPerLine }) => `
        padding-top: calc(0.5625 * calc(100% / ${$setsPerLine} - var(--gap)));
      `}

      > * {
        object-fit: contain;
      }
    }
  }
  &.grid-3 {
    .item {
      ${({ $setsPerLine }) => `
        padding-top: calc(100% / ${$setsPerLine} - var(--gap) + ((100% / ${$setsPerLine} - var(--gap)) / 3));
      `}
    }
  }
  &.grid-4 {
    .item {
      border-radius: 50%;
      #grid-variables-label {
        left: 50%;
        right: auto;
        transform: translateX(-50%);
      }
    }
  }
  &.grid-5 {
    height: var(--entity-height);
    .grid-container {
      margin: 0;
      height: 100%;
      width: 100%;
      display: grid;
      gap: var(--gap);
      grid-template-columns: repeat(${({ $setsPerLine }) => $setsPerLine}, 1fr);
      ${({ $length }) => {
        if ($length === 1)
          return css`
            grid-template-columns: 1fr;
          `;
      }}
    }

    .grid-inner {
      height: 100%;
    }

    .grid__masonry_row {
      position: relative;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      gap: var(--gap);
    }
    .item {
      padding: 0;
      margin: 0;
      flex: none;
      width: 100%;
    }
  }
  &.grid-6 {
    height: var(--entity-height);
    .grid-inner {
      height: 100%;
    }
    .grid-container {
      flex-wrap: nowrap;
      height: calc(100% + var(--gap));
    }
    .item {
      padding-top: 0;
      flex: 0 1 100%;
    }
  }

  &.grid-7 {
    height: var(--entity-height);
    .grid-inner {
      height: 100%;
    }
    .grid-container {
      flex-flow: column;
      height: calc(100% + var(--gap));
      margin: 0;
    }
    .item {
      margin: 0;
      padding-top: 0;
      height: var(--entity-height);
      flex: 0 1 100%;
      margin-bottom: var(--gap);
    }
  }

  &.grid-8 {
    --s: ${({ $setsPerLine }) => `calc(100% / ${$setsPerLine} - var(--gap))`};
    --m: calc(var(--gap) / 2);
    .grid-container {
      justify-content: flex-start;

      ${({ $setsPerLine }) =>
        $setsPerLine !== 1 &&
        css`
          padding-bottom: calc(var(--m) + var(--s) * 0.2885);
        `}
    }
    .item {
      flex: 0 0 var(--s);
      margin: var(--m);
      padding-top: calc(var(--s) * 1.1547);
      display: inline-block;
      font-size: initial;
      border-radius: 0;
      #grid-variables-label {
        left: 50%;
        right: auto;
        transform: translateX(-50%);
      }

      ${({ $radius }) =>
        $radius === "0"
          ? css`
              clip-path: polygon(
                0% 25%,
                0% 75%,
                50% 100%,
                100% 75%,
                100% 25%,
                50% 0%
              );
            `
          : css`
              clip-path: url(#grid-rounded-hexagon-clipath);
            `}

      margin-bottom: calc((var(--m) - var(--s) * ${({ $radius }) =>
        $radius === "0" ? 0.2885 : 0.2435}) -  calc(var(--gap) / 4));
      ${({ $setsPerLine }) =>
        $setsPerLine === 1 &&
        css`
          margin-bottom: var(--gap);
        `}

      ${({ $setsPerLine }) =>
        $setsPerLine === 2 &&
        css`
          &:nth-child(3n + 3) {
            margin-left: calc(var(--s) / 2 + (var(--m) * 2));
            margin-right: calc(var(--s) / 2 + (var(--m) * 2));
          }
        `}
      ${({ $setsPerLine }) =>
        $setsPerLine === 3 &&
        css`
          &:nth-child(5n + 4) {
            margin-left: calc(var(--s) / 2 + (var(--m) * 2));
          }
          &:nth-child(5n + 5) {
            margin-right: calc(var(--s) / 2 + (var(--m) * 2));
          }
        `}
      ${({ $setsPerLine }) =>
        $setsPerLine === 4 &&
        css`
          &:nth-child(7n + 5) {
            margin-left: calc(var(--s) / 2 + (var(--m) * 2));
          }
          &:nth-child(7n + 7) {
            margin-right: calc(var(--s) / 2 + (var(--m) * 2));
          }
        `}
      ${({ $setsPerLine }) =>
        $setsPerLine === 5 &&
        css`
          &:nth-child(9n + 6) {
            margin-left: calc(var(--s) / 2 + (var(--m) * 2));
          }
          &:nth-child(9n + 9) {
            margin-right: calc(var(--s) / 2 + (var(--m) * 2));
          }
        `}
      ${({ $setsPerLine }) =>
        $setsPerLine === 6 &&
        css`
          &:nth-child(11n + 7) {
            margin-left: calc(var(--s) / 2 + (var(--m) * 2));
          }
          &:nth-child(11n + 11) {
            margin-right: calc(var(--s) / 2 + (var(--m) * 2));
          }
        `}
    }
  }

  @media screen and (max-width: 768px) {
    --gap: ${({ $gap }) => Math.min(16, Number($gap))}px;
    /* Mobile width floor. A narrow grid (e.g. a 15%-wide testimonial avatar
       pair) looks fine on a wide desktop slide but collapses to a few px on the
       narrow mobile viewport — the Grid otherwise keeps its desktop width % on
       mobile. max() lifts only grids narrower than the floor; already-wide
       galleries are untouched. Tune the 40% here. */
    width: max(var(--entity-size), 40%);
  }

  .video-lib-pdf-thumbnail-contaienr {
    video {
      position: absolute;
      inset: 0px;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;

export const GridItemStyled = styled.div<{
  $hasLink: boolean;
  $overlay: boolean;
  $overlayColor: string;
  $overlayOpacity: number;
}>`
  ${({ $hasLink }) =>
    $hasLink &&
    css`
      cursor: pointer;
      img,
      video,
      .grid-item-color {
        transition:
          transform 0.3s ease-in-out,
          opacity 0.3s ease-in-out;
      }
      &:hover {
        img,
        video {
          transform: scale(1.1);
        }
        .grid-item-color {
          opacity: 0.8;
        }
      }
    `}

  ${({ $overlay, $overlayColor, $overlayOpacity }) =>
    $overlay &&
    css`
      position: relative;
      &::after {
        position: absolute;
        content: "";
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: ${$overlayColor};
        opacity: ${$overlayOpacity}%;
        z-index: 1;
      }
    `}
`;
